import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// Captured ONCE at page load, before any effect or URL cleanup runs. If we're returning from
// Google (fragment) or Apple (query) OAuth, AuthCallback owns establishing the session — so the
// provider must NOT race it with a /auth/me check (which flips the user to logged-out in Safari).
const OAUTH_RETURN =
  typeof window !== "undefined" &&
  (window.location.search.includes("apple_token=") || window.location.hash.includes("session_id="));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [usage, setUsage] = useState(null);

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  const refreshUsage = useCallback(async () => {
    try {
      const r = await api.get("/usage");
      setUsage(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Skip the /me check when returning from OAuth — AuthCallback establishes the session.
    if (OAUTH_RETURN) return;
    api.get("/auth/me").then((r) => { setUser(r.data); refreshUsage(); }).catch(() => setUser(false));
  }, [refreshUsage]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    refreshUsage();
    return data;
  };
  const loginWithGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setUser(data);
    refreshUsage();
    return data;
  };
  const loginWithAppleToken = async (token) => {
    const { data } = await api.post("/auth/apple/exchange", { token });
    setUser(data);
    refreshUsage();
    return data;
  };
  const register = async (name, email, password, inviteToken) => {
    const { data } = await api.post("/auth/register", { name, email, password, invite_token: inviteToken });
    setUser(data);
    refreshUsage();
    return data;
  };
  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    setUsage(null);
  };

  const isPro = !!user?.is_pro;

  return (
    <AuthContext.Provider value={{ user, setUser, usage, isPro, login, loginWithGoogle, loginWithAppleToken, register, logout, refreshUser, refreshUsage }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
